import React, { useState } from 'react';
import './LeftPanelHeader.scss';
import {
  IconCopyNew,
  IconAdd,
  IconClose,
  IconCancel,
  IconImportLLMResult,
  IconViewData,
  IconViewImports,
  IconLLMPreview,
  IconEdit,
  FilterConstrain,
  FilterUnconstrain,
} from 'components/Icons';

export interface TabItem {
  id: string;
  label: string;
  callback?: () => void;
}

export interface SavedResponse {
  id: string;
  title: string;
  content: string;
  timestamp: Date;
  source?: string;
}

export interface LeftPanelHeaderProps {
  tabs?: TabItem[];
  activeTabId?: string;
  onTabChange?: (tabId: string) => void;
  /**
   * List of tab ids that correspond to variable placeholders detected in the prompt.
   * These will be rendered in the second row with horizontal overflow.
   */
  variableIds?: string[];
  onCopy?: () => void;
  onPaste?: () => void;
  onClearPasted?: () => void;
  // New props for Preview tab functionality
  savedResponses?: SavedResponse[];
  selectedResponseId?: string | null;
  onResponseSelect?: (responseId: string) => void;
  onResponseDelete?: (responseId: string) => void;
  onTogglePreviewMode?: () => void;
  onOpenSavedResponses?: () => void;
  isDataMode?: boolean;
  // New props for Vars tab functionality
  onToggleVarEdit?: () => void;
  isVarEditMode?: boolean;
  // New props for constrain toggle functionality
  onToggleConstrain?: () => void;
  isConstrained?: boolean;
}

const LeftPanelHeader: React.FC<LeftPanelHeaderProps> = ({
  tabs = [],
  activeTabId,
  onTabChange,
  variableIds,
  onCopy,
  onPaste,
  onClearPasted,
  savedResponses = [],
  selectedResponseId,
  onResponseSelect,
  onResponseDelete,
  onTogglePreviewMode,
  onOpenSavedResponses,
  isDataMode = false,
  onToggleVarEdit,
  isVarEditMode = false,
  onToggleConstrain,
  isConstrained = false,
}) => {
  // Default to 'preview' if no activeTabId is provided
  const [activeTab, setActiveTab] = useState<string>(activeTabId || 'preview');

  // Create a complete tabs array with the Preview tab always first
  const allTabs: TabItem[] = [...tabs];

  // Split tabs into two rows: system tabs and variable tabs
  const systemTabs: TabItem[] = allTabs;

  // Generate variable tabs from variableIds
  const variableTabs: TabItem[] = (variableIds || []).map(variableId => ({
    id: variableId,
    label: variableId,
    callback: () => {
      // Call onTabChange to notify parent about variable selection
      if (onTabChange) {
        onTabChange(variableId);
      }
    },
  }));

  // Determine if second line should be shown
  const shouldShowSecondLine = activeTab === 'preview' || activeTab === 'vars';

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);

    // Call the callback for the selected tab if it exists
    const selectedTab = tabs.find(tab => tab.id === tabId);
    if (selectedTab?.callback) {
      selectedTab.callback();
    }

    // Call the onTabChange prop if provided
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  return (
    <div className="left-panel-header">
      {/* Row 1: System + additional (non-variable) tabs */}
      <div className="left-panel-header__tabs__container">
        <div className="left-panel-header__tabs">
          {systemTabs.map(tab => (
            <div
              key={tab.id}
              className={`left-panel-header__tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
            >
              {tab.label}
            </div>
          ))}
        </div>
        <div className="left-panel-header__tabs__actions">
          {onCopy && (
            <div className="left-panel-header__action-button" onClick={() => onCopy()} title="Copy content">
              <IconCopyNew />
            </div>
          )}
          {onToggleConstrain && (
            <div
              className="left-panel-header__action-button"
              onClick={() => onToggleConstrain()}
              title={isConstrained ? 'Switch to unconstrained view' : 'Switch to constrained view'}
            >
              {isConstrained ? <FilterConstrain /> : <FilterUnconstrain />}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Context-dependent second line */}
      {shouldShowSecondLine && (
        <div className="left-panel-header__second-line">
          {activeTab === 'preview' && (
            <>
              {/* Left side: Response tabs */}
              <div className="left-panel-header__response-tabs">
                {savedResponses.map(response => (
                  <div
                    key={response.id}
                    className={`left-panel-header__response-tab ${selectedResponseId === response.id ? 'active' : ''}`}
                    onClick={() => onResponseSelect?.(response.id)}
                  >
                    <span className="response-tab__title">{response.title}</span>
                    <div
                      className="response-tab__close"
                      onClick={e => {
                        e.stopPropagation();
                        onResponseDelete?.(response.id);
                      }}
                      title="Delete response"
                    >
                      <IconCancel />
                    </div>
                  </div>
                ))}
              </div>

              {/* Right side: Action buttons */}
              <div className="left-panel-header__preview-actions">
                {onPaste && (
                  <div
                    className="left-panel-header__action-button"
                    onClick={() => onPaste()}
                    title="Paste LLM response"
                  >
                    <IconImportLLMResult />
                  </div>
                )}
                {onTogglePreviewMode && (
                  <div
                    className={`left-panel-header__action-button ${isDataMode ? 'active' : ''}`}
                    onClick={() => onTogglePreviewMode()}
                    title={isDataMode ? 'Switch to preview' : 'Switch to data view'}
                  >
                    {isDataMode ? <IconLLMPreview /> : <IconViewData />}
                  </div>
                )}
                {onOpenSavedResponses && (
                  <div
                    className={`left-panel-header__action-button ${activeTabId === 'saved-responses' ? 'active' : ''}`}
                    onClick={() => onOpenSavedResponses()}
                    title={activeTabId === 'saved-responses' ? 'Back to preview' : 'Open saved responses'}
                  >
                    <IconViewImports />
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'vars' && (
            <>
              {/* Left side: Variable tabs */}
              <div className="left-panel-header__variable-tabs">
                {variableTabs.map(tab => (
                  <div
                    key={tab.id}
                    className={`left-panel-header__tab ${activeTabId === tab.id ? 'active' : ''}`}
                    onClick={() => tab.callback?.()}
                  >
                    {tab.label}
                  </div>
                ))}
              </div>

              {/* Right side: Edit button */}
              <div className="left-panel-header__vars-actions">
                {onToggleVarEdit && (
                  <div
                    className={`left-panel-header__action-button ${isVarEditMode ? 'active' : ''}`}
                    onClick={() => onToggleVarEdit()}
                    title={isVarEditMode ? 'Exit edit mode' : 'Edit variable content'}
                  >
                    <IconEdit />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LeftPanelHeader;
